import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiCallWizard } from '../../../actions/wizards';
import { getAccessToken } from '../../../storage/accessToken';
import { getSelectedClientHydrationKey } from '../../../utils/functions';
import { customerType, statusOk } from '../../../utils/variables';
import { getAllData, validateAccess } from '../functions';

export const useUserTriggeredOpenState = ({ disabled = false, resetDeps = [] } = {}) => {
  const [open, setOpen] = useState(false);

  // Ignore synthetic/autofocus open attempts; only user gestures (or explicit force) can open.
  const handleOpen = (event, force = false) => {
    if (disabled) return;
    const eventType = event?.type;
    const isUserTriggered = ['click', 'mousedown', 'keydown', 'touchstart'].includes(
      eventType,
    );
    if (!force && !isUserTriggered) return;
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Close stale menu state whenever caller-provided context changes.
  useEffect(() => {
    setOpen(false);
  }, resetDeps);

  return {
    open,
    handleOpen,
    handleClose,
  };
};

export const useValidateAccess = inputValue => {
  const accessToken = getAccessToken();
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const customerVerified =
    useSelector(state => state?.auth?.usertype === customerType) && accessToken;

  const valid = validateAccess(inputValue, isAuthenticated, customerVerified);

  return valid;
};

export const useOnChangeApiCalls = storeState => {
  const dispatch = useDispatch();
  // const storeState = useSelector(state => state);
  return ({ apiCalls, onErrorCall = () => { }, onSuccessCall = () => { } }) => {
    if (apiCalls && apiCalls.length > 0 && !apiCalls.includes(undefined)) {
      getAllData(apiCalls, apiCallWizard.apiCallWizardAction, dispatch, storeState)
        .then(resp => {
          // // console.log(resp);
          const responseTypeSuccess =
            resp && resp.length > 0
              ? resp.every(x => x?.responseType === statusOk || false)
              : false;
          // console.log('responseTypeSuccess: ', responseTypeSuccess);
          // // console.log('resetWizardData: ', resetWizardData);
          // setLoading(false);
          if (responseTypeSuccess || !resp) {
            onSuccessCall();
          } else {
            onErrorCall();
          }
        })
        .catch(e => {
          console.log('api call error: ', e);
          onErrorCall();
          // setLoading(false);
        });
    } else {
      onSuccessCall();
    }
  };
};

export const useClientBackedEmailHydrationState = ({
  isClientBackedEmailField,
  selectedClient,
}) => {
  const userEditedClientEmailRef = useRef(false);
  const hydratedClientKeyRef = useRef(null);

  const selectedClientHydrationKey = isClientBackedEmailField
    ? getSelectedClientHydrationKey(selectedClient)
    : null;

  useEffect(() => {
    if (!isClientBackedEmailField) return;

    if (selectedClientHydrationKey !== hydratedClientKeyRef.current) {
      hydratedClientKeyRef.current = selectedClientHydrationKey;
      userEditedClientEmailRef.current = false;
    }
  }, [isClientBackedEmailField, selectedClientHydrationKey]);

  const shouldSkipClientEmailHydration =
    isClientBackedEmailField &&
    userEditedClientEmailRef.current &&
    selectedClientHydrationKey === hydratedClientKeyRef.current;

  const markClientEmailEdited = () => {
    if (isClientBackedEmailField) {
      userEditedClientEmailRef.current = true;
    }
  };

  return {
    shouldSkipClientEmailHydration,
    markClientEmailEdited,
  };
};

// JSON-driven field-clear hook. Fires `onClear` when any field listed in
// Fires `onClear` when any field listed in `resetFields` transitions from
// truthy → falsy in `allWizardData`. Fully driven by wizard JSON config —
// no hardcoded field names. Components opt in via resetOnChange + resetFields.
export const useFieldClearTransition = ({
  enabled,      // Boolean – typically: resetOnChange && resetFields.length > 0
  resetFields,  // array of { name, staticValue } from wizard JSON
  allWizardData,// current wizards.allInitialWizardData from Redux
  onClear,      // callback invoked when a trigger field clears
}) => {
  const watchedValues = enabled
    ? (resetFields || []).reduce((acc, f) => {
      if (f?.name) acc[f.name] = allWizardData?.[f.name];
      return acc;
    }, {})
    : {};

  const previousValuesRef = useRef(watchedValues);

  useEffect(() => {
    const currentValues = watchedValues;

    if (!enabled || !Object.keys(currentValues).length) {
      previousValuesRef.current = currentValues;
      return;
    }

    const previousValues = previousValuesRef.current || {};

    const hasClearTransition = Object.keys(currentValues).some(key =>
      !!previousValues[key] && !currentValues[key]
    );

    if (hasClearTransition && typeof onClear === 'function') {
      onClear();
    }

    previousValuesRef.current = currentValues;
  }, [enabled, watchedValues, onClear]);
};
